import type { Agent } from './types'

/**
 * The mailing list — who Renee has ticked, right now.
 *
 * This is deliberately **not** a tag. A tag is a list that persists: it survives
 * closing the browser, it can be filtered on, and it is what a recurring
 * campaign points at. A selection is a list for one job — tick twelve people,
 * email them, done. Nothing is written to the database and there is nothing to
 * clean up afterwards.
 *
 * **It is a Set, and that is the whole answer to duplicates.** Select everyone
 * in the Charleston area, then everyone on the Charleston MLS: the people in
 * both are already in, so adding them again is a no-op. The overlap cannot
 * produce a duplicate by construction rather than by a check somebody has to
 * remember to write.
 *
 * **It also survives a filter change, on purpose.** That is what makes it
 * useful: every filter in this dashboard narrows — area AND MLS AND team — so
 * there is no way to ask for "Charleston area OR Charleston MLS". Ticking
 * across two filters is how that question gets asked. The consequence is that
 * agents can be selected while not visible, which is why the count is always on
 * the toolbar and why "Show only these" exists.
 */
export type Selection = ReadonlySet<string>

export const EMPTY: Selection = new Set<string>()

/**
 * Add agents to the selection, and say how many were genuinely new.
 *
 * The count matters as much as the result. "Select all" on a second, overlapping
 * filter must be able to say *40 match, 31 already selected, add 9* — otherwise
 * Renee has to trust that the overlap was handled instead of reading it.
 */
export function addAll(
  selection: Selection,
  ids: readonly string[],
): { next: Selection; added: string[] } {
  const next = new Set(selection)
  const added: string[] = []
  for (const id of ids) {
    if (next.has(id)) continue
    next.add(id)
    added.push(id)
  }
  return { next, added }
}

/** Remove agents from the selection, and say which were actually in it. */
export function removeAll(
  selection: Selection,
  ids: readonly string[],
): { next: Selection; removed: string[] } {
  const next = new Set(selection)
  const removed: string[] = []
  for (const id of ids) {
    if (!next.has(id)) continue
    next.delete(id)
    removed.push(id)
  }
  return { next, removed }
}

export function toggle(selection: Selection, id: string): Selection {
  const next = new Set(selection)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

/**
 * Split a list of agents by whether they are already selected.
 *
 * This is what puts both numbers on screen before the button is pressed, in the
 * same shape the export counts and the tag menu already use.
 */
export function splitBySelection(
  agents: readonly Agent[],
  selection: Selection,
): { already: string[]; fresh: string[] } {
  const already: string[] = []
  const fresh: string[] = []
  for (const a of agents) (selection.has(a.rezen_id) ? already : fresh).push(a.rezen_id)
  return { already, fresh }
}

/**
 * How many of the selection are in the list currently on screen.
 *
 * Not the same as the total, and saying the wrong one is misleading: 49 people
 * can be selected while the current filter only shows 9 of them. The toolbar
 * shows the total; this answers "how many of the ones I can see".
 */
export function visibleCount(agents: readonly Agent[], selection: Selection): number {
  let n = 0
  for (const a of agents) if (selection.has(a.rezen_id)) n++
  return n
}

/**
 * Drop anyone from the selection who is no longer in the roster at all.
 *
 * An id removed at cutover, or one that never existed, would otherwise sit in
 * the count forever — "49 selected" above a list that can only ever show 48,
 * with no way to find the missing one. Reconciling keeps the number answerable.
 *
 * **Returns the same Set when nothing changed.** The effect that calls this
 * feeds its result straight back into state; a fresh Set every time would
 * re-trigger the effect forever.
 *
 * Departed agents are deliberately NOT dropped. They can only be on the list by
 * being ticked, "Show only these" displays them, and Renee can untick them —
 * removing them here would silently undo a choice she made.
 */
export function pruneToRoster(selection: Selection, all: readonly Agent[]): Selection {
  const live = new Set(all.map((a) => a.rezen_id))
  const next = new Set<string>()
  for (const id of selection) if (live.has(id)) next.add(id)
  return next.size === selection.size ? selection : next
}

/**
 * What an undo of the last change to the list would put back.
 *
 * Every way of shrinking the list — Clear all, "Take off N" (which on an
 * unfiltered All tab removes every single person), cancelling the composer, and
 * a send that went through — records one of these. The scope travels with it
 * because restoring 273 people while leaving the screen showing all 785 is how
 * Tag ends up writing 785 rows.
 *
 * `what` exists so the button can say what happened rather than just offering a
 * number. A bare "Put back 40" after a send is a control whose only meaning is
 * "re-arm the group you have just emailed", and it sits one click from Email 40
 * — worth naming out loud.
 */
export type SelectionUndo = {
  /** The selection as it was, restored wholesale. */
  previous: Selection
  /** Whether "Show only these" was on at the time. */
  selectedOnly: boolean
  /** How many the undo would put back, for the button to say out loud. */
  count: number
  what: 'cleared' | 'removed' | 'sent'
}
